import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, Link } from "@tanstack/solid-router";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  IconCalendar,
  IconCircleDollarSign,
  IconEllipsisVertical,
  IconLandmark,
  IconPiggyBank,
  IconPrinter,
  IconScale,
} from "~/components/icons";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { DatePickerField } from "~/components/ui/date-picker-field";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { getBalanceSheetPageData } from "~/features/reports/balance-sheet-page";
import {
  BalanceSheetResource,
  defaultBalanceSheetParams,
  todayIso,
  type BalanceSheetData,
} from "~/features/reports/balance-sheet.resource";
import { withBusinessGuard } from "~/lib/route-guards";
import { cn, formatCurrency, formatDisplayCalendarDate } from "~/lib/utils";

export const Route = createFileRoute(
  "/_dashboard/dashboard/reports/balance-sheet",
)({
  staticData: {
    breadcrumb: "Balance Sheet",
  },
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    // One server-side function call — aggregate fetches the default balance
    // sheet and business detail in parallel. During SSR this is a direct
    // function call; on client navigation it's one HTTP round trip.
    const pageData = await getBalanceSheetPageData({ data: { businessId } });

    // Seed the query cache so the balance sheet component's createQuery hook
    // finds data on the first paint (including SSR on a hard refresh).
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      pageData.businessDetail,
    );
    BalanceSheetResource.seedIfEmpty(
      context.queryClient,
      defaultBalanceSheetParams(businessId),
      pageData.balanceSheet,
    );

    return {
      businessId,
      balanceSheet: pageData.balanceSheet,
      collection,
      activeBusiness: pageData.businessDetail,
    };
  },
  component: BalanceSheetPage,
});

function BalanceSheetPage() {
  const qc = useQueryClient();
  const data = Route.useLoaderData();

  // Surface loader data into the TQ cache synchronously during render, so the
  // balance sheet component's createQuery hooks never fire a network request on
  // the initial hydration pass.
  const d = data();
  if (d) {
    businessDetailResource.seedIfEmpty(qc, d.businessId, d.activeBusiness);
    BalanceSheetResource.seedIfEmpty(
      qc,
      defaultBalanceSheetParams(d.businessId),
      d.balanceSheet,
    );
  }

  return <BalanceSheetReport />;
}

// Shared column layout for the statement: account name + one right-aligned
// numeric column (amount).
const GRID = "grid grid-cols-[minmax(0,1fr)_7rem] items-center gap-4";

function BalanceSheetReport() {
  const businessId = useActiveBusinessId();

  // As-of date state. Defaults to today.
  const [asOf, setAsOf] = createSignal(todayIso());

  const bsQuery = createQuery(() => ({
    ...BalanceSheetResource.options(
      {
        businessId: businessId() ?? "",
        asOf: asOf(),
      },
      !!businessId(),
    ),
    // Keep the previous as-of date's data visible while the new date is
    // fetching, so the statement doesn't flash empty on every date change.
    placeholderData: keepPreviousData,
  }));

  // Read the balance sheet data reactively from the live query. `bsQuery.data`
  // is backed by solid-query's `queryResource.latest` proxy, which is reactive —
  // it updates whenever the query for the current as-of date resolves, and with
  // `keepPreviousData` it holds the prior date's data while a new date is
  // fetching (no flash). Reading it directly (rather than via a non-reactive
  // `qc.getQueryData` cache snapshot) keeps the memo correctly subscribed so a
  // background refetch for an already-cached key still retriggers the UI.
  const bs = createMemo<BalanceSheetData | undefined>(() => bsQuery.data);

  const asOfLabel = createMemo(() => {
    const a = asOf();
    return a ? (formatDisplayCalendarDate(a, { style: "short" }) ?? "") : "";
  });

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      {/* Report header */}
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 class="text-2xl font-bold tracking-tight">Balance Sheet</h1>
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <Show when={asOfLabel()}>
                <Badge
                  variant="outline"
                  class="gap-1.5 font-normal text-muted-foreground"
                >
                  <IconCalendar class="size-3" />
                  As of {asOfLabel()}
                </Badge>
              </Show>
            </div>
          </div>
          <DropdownMenu placement="bottom-end">
            <DropdownMenuTrigger
              as={Button<"button">}
              variant="outline"
              size="icon"
              class="print:hidden"
            >
              <IconEllipsisVertical class="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent class="w-44">
              <DropdownMenuItem onClick={() => window.print()}>
                <IconPrinter class="size-4" />
                Print Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Toolbar */}
        <div class="flex flex-wrap items-center gap-2 print:hidden">
          <div class="flex items-center gap-1.5">
            <span class="shrink-0 text-sm text-muted-foreground">As of</span>
            <DatePickerField value={asOf()} onChange={setAsOf} class="w-56" />
          </div>
        </div>
      </div>

      {/* Financial statement */}
      {/* Use the function-as-children form of Show: Solid's Show only re-runs
          its children when `when` flips between falsy/truthy, NOT when a truthy
          value changes. Passing `data={bs()!}` directly would pin the children
          to the first snapshot forever. The accessor form re-reads `bs()` fresh
          on every render pass, so the statement updates on each fetch. */}
      <Show when={bs()}>
        {(data) => (
          <>
            <BsSummaryCards data={data()} />
            <BalanceSheetStatement data={data()} />
          </>
        )}
      </Show>
    </div>
  );
}

/**
 * At-a-glance KPI row. Lets someone read the headline result without scanning
 * the full statement.
 */
function BsSummaryCards(props: { data: BalanceSheetData }) {
  const cards = createMemo(() => [
    {
      label: "Total Assets",
      value: formatCurrency(props.data.totalAssets),
      color: "var(--color-account-asset)",
      rgb: "91,141,239",
      icon: IconCircleDollarSign,
    },
    {
      label: "Total Liabilities",
      value: formatCurrency(props.data.totalLiabilities),
      color: "var(--color-account-liability)",
      rgb: "240,122,106",
      icon: IconLandmark,
    },
    {
      label: "Total Equity",
      value: formatCurrency(props.data.totalEquity),
      color: "var(--color-account-equity)",
      rgb: "62,207,174",
      icon: IconPiggyBank,
    },
    {
      label: "Liabilities & Equity",
      value: formatCurrency(
        props.data.totalLiabilities + props.data.totalEquity,
      ),
      color: "var(--color-primary)",
      rgb: "120,120,140",
      icon: IconScale,
    },
  ]);

  return (
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <For each={cards()}>
        {(card) => (
          <div class="relative flex flex-col overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-sm">
            {/* Per-card top accent bar */}
            <div
              class="h-0.5 w-full shrink-0"
              style={{ background: card.color }}
            />

            {/* Card header */}
            <div class="px-5 pb-3 pt-5">
              <div class="mb-3.5 flex items-center gap-2.5">
                <div
                  class="flex size-8.5 shrink-0 items-center justify-center rounded-[10px]"
                  style={{
                    background: `rgba(${card.rgb},0.15)`,
                    color: card.color,
                  }}
                >
                  <card.icon size={16} />
                </div>
                <span class="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  {card.label}
                </span>
              </div>

              <div class="font-mono text-[26px] font-medium tracking-[-0.02em]">
                {card.value}
              </div>
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

function BalanceSheetStatement(props: { data: BalanceSheetData }) {
  return (
    <div class="overflow-x-auto rounded-lg border">
      <div class="min-w-140">
        {/* Column headers */}
        <div
          class={cn(
            GRID,
            "sticky top-0 z-10 border-b bg-muted/40 px-4 py-2.5 text-xs font-medium text-muted-foreground",
          )}
        >
          <span>Account</span>
          <span class="text-right">Amount</span>
        </div>

        <div class="px-4">
          <BsSection
            title="Assets"
            lines={props.data.assets}
            total={props.data.totalAssets}
          />
          <BsSection
            title="Liabilities"
            lines={props.data.liabilities}
            total={props.data.totalLiabilities}
          />
          <BsSection
            title="Equity"
            lines={props.data.equity}
            total={props.data.totalEquity}
          />
        </div>

        {/* Bottom total */}
        <div
          class={cn(
            GRID,
            "border-t-2 bg-muted/20 px-4 py-3 text-sm font-semibold",
          )}
        >
          <span>Total Liabilities &amp; Equity</span>
          <span class="text-right tabular-nums">
            {formatCurrency(
              props.data.totalLiabilities + props.data.totalEquity,
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

function BsSection(props: {
  title: string;
  lines: BalanceSheetData["assets"];
  total: number;
}) {
  return (
    <div class="py-1">
      {/* Section header */}
      <div class={cn(GRID, "py-2")}>
        <span class="text-sm font-semibold">{props.title}</span>
      </div>

      <Show
        when={props.lines.length > 0}
        fallback={
          <p class="py-2 pl-4 text-sm text-muted-foreground">
            No {props.title.toLowerCase()} accounts with balances as of this
            date.
          </p>
        }
      >
        <div class="divide-y divide-border/60">
          <For each={props.lines}>{(line) => <BsRow line={line} />}</For>
        </div>
      </Show>

      {/* Subtotal */}
      <div class={cn(GRID, "border-t py-2.5 text-sm font-semibold")}>
        <span class="pl-4">Total {props.title}</span>
        <span class="text-right tabular-nums">
          {formatCurrency(props.total)}
        </span>
      </div>
    </div>
  );
}

function BsRow(props: { line: BalanceSheetData["assets"][number] }) {
  return (
    <div class={cn(GRID, "rounded py-1.5 text-sm hover:bg-muted/30")}>
      <span class="flex items-center gap-2 truncate">
        <span class="shrink-0 font-mono text-[11px] text-muted-foreground/70">
          {props.line.code}
        </span>
        <Link
          to="/dashboard/accounts/$accountId"
          params={{ accountId: props.line.id }}
          class="truncate hover:underline"
        >
          {props.line.name}
        </Link>
      </span>
      <span class="text-right tabular-nums">
        {formatCurrency(props.line.amount)}
      </span>
    </div>
  );
}
